import { useState } from "react";
import { X, Plus, Loader2 } from "lucide-react";
import { cn } from "@/lib/util";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { DurationPicker } from "@/components/security/client/LicensePanel";

export function AddClientModal({ onClose, onCreated }) {
  const { t } = useT();
  const [name, setName] = useState("");
  const [industry, setIndustry] = useState("");
  const [country, setCountry] = useState("");
  const [criticality, setCriticality] = useState("medium");
  const [domains, setDomains] = useState("");
  const [ips, setIps] = useState("");
  const [months, setMonths] = useState(12);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await api.post("/clients", {
        name, industry: industry || null, country: country || null, criticality,
        root_domains: domains.split(",").map(s => s.trim()).filter(Boolean),
        ip_ranges: ips.split(",").map(s => s.trim()).filter(Boolean),
        license_duration_months: months,
      });
      onCreated();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || t("add_client.error_create"));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-card border border-border p-6 space-y-4"
        data-testid="add-client-modal"
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="overline">{t("add_client.overline")}</div>
            <h2 className="font-display text-2xl font-medium mt-1">{t("add_client.title")}</h2>
          </div>
          <button type="button" onClick={onClose} className="p-1 hover:bg-muted" data-testid="close-add-client">
            <X className="w-4 h-4" />
          </button>
        </div>

        <Field label={t("add_client.name")} required>
          <input value={name} onChange={(e) => setName(e.target.value)} required data-testid="input-client-name"
                 className="w-full bg-background border border-border px-3 py-2 text-sm font-mono focus:border-primary outline-none" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("add_client.industry")}>
            <input value={industry} onChange={(e) => setIndustry(e.target.value)} data-testid="input-client-industry"
                   className="w-full bg-background border border-border px-3 py-2 text-sm font-mono focus:border-primary outline-none" />
          </Field>
          <Field label={t("add_client.country")}>
            <input value={country} onChange={(e) => setCountry(e.target.value)} data-testid="input-client-country"
                   className="w-full bg-background border border-border px-3 py-2 text-sm font-mono focus:border-primary outline-none" />
          </Field>
        </div>
        <Field label={t("add_client.criticality")}>
          <div className="flex gap-1 border border-border p-1">
            {["low", "medium", "high", "critical"].map(c => (
              <button key={c} type="button" onClick={() => setCriticality(c)}
                      className={cn("font-mono text-[10px] tracking-widest uppercase px-3 py-1 flex-1",
                        criticality === c ? "bg-primary/20 text-primary" : "text-muted-foreground hover:text-foreground")}
                      data-testid={`crit-${c}`}>{t(`add_client.crit_${c}`)}</button>
            ))}
          </div>
        </Field>
        <Field label={t("add_client.domains")}>
          <input value={domains} onChange={(e) => setDomains(e.target.value)} placeholder={t("add_client.domains_placeholder")}
                 className="w-full bg-background border border-border px-3 py-2 text-sm font-mono focus:border-primary outline-none"
                 data-testid="input-client-domains" />
        </Field>
        <Field label={t("add_client.ips")}>
          <input value={ips} onChange={(e) => setIps(e.target.value)} placeholder={t("add_client.ips_placeholder")}
                 className="w-full bg-background border border-border px-3 py-2 text-sm font-mono focus:border-primary outline-none"
                 data-testid="input-client-ips" />
        </Field>
        <Field label="Duración de la licencia (desde hoy)" required>
          <DurationPicker value={months} onChange={setMonths} prefix="license-months" />
        </Field>

        {err && <div className="border border-sev-critical/50 bg-sev-critical/10 text-sev-critical text-xs font-mono px-3 py-2">{err}</div>}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="border border-border px-4 py-2 text-sm hover:bg-muted" data-testid="cancel-add-client">{t("add_client.cancel")}</button>
          <button type="submit" disabled={busy || !name}
                  className="bg-primary text-primary-foreground px-4 py-2 text-sm hover:bg-primary/90 disabled:opacity-50 inline-flex items-center gap-2"
                  data-testid="submit-add-client">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
            {t("add_client.submit")}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground mb-1">
        {label}{required && <span className="text-sev-critical ml-1">*</span>}
      </div>
      {children}
    </label>
  );
}
